import { useEffect, useState } from "react"
import { useAtom as GetAtom } from "jotai"
import {
  checkoutDiscountAtom,
  checkoutIsNeedCalculateSumBackAtom,
  checkoutModalDiscountOpenAtom,
  checkoutSumGiveAtom,
  checkoutSumsAtom,
  checkoutSumTotalAtom,
} from "../../store/checkout"
import { formatPriceKZT } from "@malond/utils"
import * as Yup from "yup"
import { ItemNumberValidation } from "../../libs/validation/validation"
import { useFormik } from "formik"
import FormError from "../Elements/Form/FormError"
import Label from "../Elements/Form/Label"
import Input from "../Elements/Form/Input"
import Select from "../Elements/Form/Select"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@malond/ui/components/dialog"
import { Button } from "@malond/ui/components/button"

interface ValuesProps {
  sumDiscount: string
  percentDiscount: string
}

const validationSchema = Yup.object({
  sumDiscount: ItemNumberValidation,
})

function CheckoutModalDiscount() {
  // Atoms
  const [isNeedCalculateSumBack] = GetAtom(checkoutIsNeedCalculateSumBackAtom)
  const [{ priceDiscount }] = GetAtom(checkoutSumsAtom)
  const [checkoutDiscount, setCheckoutDiscount] = GetAtom(checkoutDiscountAtom)
  const [checkoutSumTotal] = GetAtom(checkoutSumTotalAtom)
  const [, setCheckoutSumGive] = GetAtom(checkoutSumGiveAtom)

  const [open, setOpen] = GetAtom(checkoutModalDiscountOpenAtom)
  const [isDisabledSelect, setIsDisabledSelect] = useState(false)

  const percentValues: Array<{ id: number; value: string }> = Array(100)
    .fill([], 0, 100)
    .map((_, i) => {
      return { id: i, value: `${i} %` }
    })

  const onSubmit = () => {
    // noop
  }

  const formik = useFormik({
    initialValues: {
      sumDiscount: checkoutDiscount || "0",
      percentDiscount: "0",
    } as ValuesProps,
    validationSchema: validationSchema,
    onSubmit: onSubmit,
  })

  useEffect(() => {
    const sumDiscountNum = Number(checkoutDiscount) || 0

    if (sumDiscountNum === 0) {
      setIsDisabledSelect(false)
    }

    const priceDiscountNum = Number(priceDiscount) || 0

    const percentDiscountNum =
      priceDiscountNum > 0 ? (sumDiscountNum / priceDiscountNum) * 100 : 0

    // Set sum
    formik.setFieldValue("sumDiscount", String(sumDiscountNum), true)

    // Check, is need to set percents?
    if (Math.round(percentDiscountNum) % 2 !== 0 || percentDiscountNum > 0) {
      formik.setFieldValue(
        "percentDiscount",
        String(Math.round(percentDiscountNum)),
        true
      )
    }
  }, [checkoutDiscount])

  useEffect(() => {
    if (isNeedCalculateSumBack) {
      setCheckoutSumGive(checkoutSumTotal)
    }
  }, [checkoutSumTotal])

  const changeSumDiscount = async (sumDiscount: number) => {
    const val = Number(sumDiscount) || 0
    await setCheckoutDiscount(String(Math.round(val * 100) / 100))
  }

  const onChangeSumValue = async (fieldName: string, value: any) => {
    const sumDiscountNum = Number(value) || 0
    const priceDiscountNum = Number(priceDiscount) || 0

    if (sumDiscountNum >= priceDiscountNum) {
      return false
    }

    setIsDisabledSelect(sumDiscountNum !== 0)

    await formik.setFieldValue(fieldName, String(sumDiscountNum), true)

    if (fieldName === "sumDiscount") {
      await changeSumDiscount(sumDiscountNum)
    }
  }

  const onChangePercentValue = async (fieldName: string, value: any) => {
    const percentDiscountNum = Number(value) || 0

    if (percentDiscountNum < 0 || percentDiscountNum > 100) {
      return false
    }

    const priceDiscountNum = Number(priceDiscount) || 0
    const sumDiscountNum = Math.ceil(
      (priceDiscountNum * percentDiscountNum) / 100
    )

    await formik.setFieldValue(
      "percentDiscount",
      String(percentDiscountNum),
      true
    )

    await changeSumDiscount(sumDiscountNum)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      saveDiscount()
    }
  }

  const closeModal = () => {
    setOpen(false)
  }

  const cancelDiscount = async () => {
    await formik.setFieldValue("sumDiscount", "0", true)
    await formik.setFieldValue("percentDiscount", "0", true)
    await changeSumDiscount(0)
    closeModal()
  }

  const saveDiscount = () => {
    closeModal()
  }

  const isClearDiscountEnabled = (Number(checkoutDiscount) || 0) > 0

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-foreground">
            Скидка на чек {formatPriceKZT(checkoutDiscount)}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Укажите фиксированную сумму скидки или процент от стоимости корзины.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={formik.handleSubmit} className="space-y-4 py-2">
          {/* Sum */}
          <div>
            <Label
              htmlFor="sumDiscount"
              isError={Boolean(
                formik.errors.sumDiscount && formik.touched.sumDiscount
              )}
            >
              Сумма скидки (₸)
            </Label>
            <Input
              id="sumDiscount"
              type="number"
              name="sumDiscount"
              value={formik.values.sumDiscount || "0"}
              onKeyDown={handleKeyDown}
              onChange={(e) => onChangeSumValue("sumDiscount", e.target.value)}
              onBlur={(e) => onChangeSumValue("sumDiscount", e.target.value)}
              withLabel={true}
              isError={Boolean(
                formik.errors.sumDiscount && formik.touched.sumDiscount
              )}
            />

            <FormError
              isError={Boolean(
                formik.errors.sumDiscount && formik.touched.sumDiscount
              )}
            >
              {formik.errors.sumDiscount}
            </FormError>
          </div>

          {/* Percent */}
          <div>
            <Label
              htmlFor="percentDiscount"
              isError={Boolean(
                formik.errors.percentDiscount && formik.touched.percentDiscount
              )}
            >
              Процент скидки (%)
            </Label>

            <Select
              name="percentDiscount"
              isDisabled={isDisabledSelect}
              value={formik.values.percentDiscount || "0"}
              values={percentValues}
              keyTitle="value"
              onChange={(e) =>
                onChangePercentValue("percentDiscount", e.target.value)
              }
              withLabel={true}
              isError={Boolean(
                formik.errors.percentDiscount && formik.touched.percentDiscount
              )}
            />

            <FormError
              isError={Boolean(
                formik.errors.percentDiscount && formik.touched.percentDiscount
              )}
            >
              {formik.errors.percentDiscount}
            </FormError>
          </div>
        </form>

        <DialogFooter className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={cancelDiscount}>
            {isClearDiscountEnabled
              ? `Убрать скидку ${formatPriceKZT(checkoutDiscount)}`
              : "Закрыть"}
          </Button>
          <Button
            type="button"
            className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
            onClick={saveDiscount}
          >
            Сохранить скидку
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CheckoutModalDiscount
